import React from "react";
import { Box, Button, Paper, Typography, CircularProgress, Stack } from "@mui/material";
import { Guideline } from "../../api";
import { CeleryTask } from "../../api/celery";
import GuidelineCard from "./GuidelineCard";
import LanguageStats from "./LanguageStats";
import Filters from "./Filters/Filters";
import EmbedTasksPanel from "./EmbedTasksPanel/EmbedTasksPanel";
import * as S from "./style";

interface LanguageCount {
  language: string;
  count: number;
}

interface GuidelinesPageProps {
  guidelines: Guideline[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
  page: number;
  totalPages: number;
  total: number;
  statusFilter: string;
  chunkFilter: string;
  onStatusChange: (value: string) => void;
  onChunkFilterChange: (value: string) => void;
  topLanguages: LanguageCount[];
  onNavigateDetail: (id: string) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onCreateClick: () => void;
  embedTasks: CeleryTask[];
  embedTasksLoading: boolean;
  embedPanelExpanded: boolean;
  onEmbedPanelToggle: () => void;
}

const GuidelinesPage: React.FC<GuidelinesPageProps> = ({
  guidelines,
  loading,
  search,
  onSearchChange,
  onSearch,
  page,
  totalPages,
  total,
  topLanguages,
  onNavigateDetail,
  onPreviousPage,
  onNextPage,
  statusFilter,
  chunkFilter,
  onStatusChange,
  onChunkFilterChange,
  onCreateClick,
  embedTasks,
  embedTasksLoading,
  embedPanelExpanded,
  onEmbedPanelToggle,
}) => {
  if (loading && guidelines.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Two-column header block */}
      <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "stretch" }}>
        {/* Left: title + language stats stacked */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={S.StyledHeaderBox}>
              <Typography sx={S.StyledHeaderTitle}>{total} Guideline{total !== 1 ? "s" : ""}</Typography>
          </Box>
          <LanguageStats topLanguages={topLanguages} />
        </Box>

        {/* Right: embedding tasks panel, full height */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EmbedTasksPanel
            tasks={embedTasks}
            loading={embedTasksLoading}
            expanded={embedPanelExpanded}
            onToggle={onEmbedPanelToggle}
          />
        </Box>
      </Box>

      {/* Filters + Search + Create — single row */}
      <Filters
        chunkRange={chunkFilter}
        onChunkRangeChange={onChunkFilterChange}
        onCreateClick={onCreateClick}
        search={search}
        onSearchChange={onSearchChange}
        onSearch={onSearch}
      />

      {/* Guidelines Grid */}
      {guidelines.length === 0 ? (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No guidelines found
          </Typography>
        </Paper>
      ) : (
        <Box sx={S.StyledGuidelinesGrid}>
          {guidelines.map((g) => (
            <GuidelineCard
              key={g.guideline_docs_id}
              guideline={g}
              onClick={() => onNavigateDetail(g.guideline_docs_id)}
            />
          ))}
        </Box>
      )}
      {totalPages > 1 && (
        <Stack {...S.StyledPaginationStack}>
          <Button
            variant="outlined"
            disabled={page <= 1}
            onClick={onPreviousPage}
            sx={{
              color: 'text.primary',
              borderColor: 'divider',
            }}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page} of {totalPages}
          </Typography>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={onNextPage}
            sx={{
              color: 'text.primary',
              borderColor: 'divider',
            }}
          >
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default GuidelinesPage;
